import React, { memo, useCallback } from 'react'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import { Switch } from 'transfix-ui/components/Switch'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Typography } from 'transfix-ui/core'
import { IGlobalMessageCta } from 'types/globalMessaging'
import {
  useLaneFiltersContext,
  useSavedLanesContext,
  useUserContext,
} from 'apps/fleet-planner/context'
import { useSavedLaneMutations } from 'apps/fleet-planner/hooks/useSavedLaneMutations'
import { findSavedLane } from '../utils'
import { useSavedLaneSwitchStyles } from './useSavedLaneSwitchStyles'

const FAILURE_MESSAGE = 'Server error. Contact tech support for assistance.'
const DELETE_SUCCESS_MESSAGE = 'You will no longer receive emails about this lane.'
const ADD_SUCCESS_MESSAGE = 'We’ll send you emails as loads become available.'
const ADD_SUCCESS_CTA: IGlobalMessageCta = {
  label: 'Manage Saved Lanes',
  url: `/fms/transfix-lanes`,
}

export interface ISavedLaneSwitchProps {}

const SavedLaneSwitch = memo<ISavedLaneSwitchProps>(() => {
  const { openToast } = useGlobalToast()
  const classes = useSavedLaneSwitchStyles()

  /**
   * Current User
   */
  const { userData } = useUserContext()
  const { motorCarriers = [] } = userData || {}
  const [motorCarrier] = motorCarriers
  const { id: motorCarrierId } = motorCarrier || {}
  const hasMultipleCarriers = motorCarriers.length > 1

  /**
   * Search filters
   */
  const { params } = useLaneFiltersContext()
  const { filters } = params

  /**
   * Saved Lanes
   */
  const {
    savedLanes = [],
    updateSavedLanesState,
    removeSavedLaneFromState,
  } = useSavedLanesContext()

  /**
   * Current saved lane if already saved
   */
  const currentSavedLane = findSavedLane(filters, savedLanes)
  const { id: savedLaneId } = currentSavedLane || {}
  const isLaneSaved = !!currentSavedLane

  /**
   * Restful mutations
   */
  const { createSavedLaneFromSearch, deleteSavedLane, loading } = useSavedLaneMutations()

  const showErrorToast = useCallback(() => {
    openToast({ toastMessage: FAILURE_MESSAGE, toastType: 'alert' })
  }, [openToast])

  const showSuccessToast = useCallback(() => {
    openToast({
      toastType: 'default',
      toastMessage: isLaneSaved ? DELETE_SUCCESS_MESSAGE : ADD_SUCCESS_MESSAGE,
      toastCta: isLaneSaved ? undefined : ADD_SUCCESS_CTA,
    })
  }, [isLaneSaved, openToast])

  const addLane = useCallback(async () => {
    if (!filters) {
      return
    }

    const response = await createSavedLaneFromSearch(filters, motorCarrierId)
    if (response) {
      showSuccessToast()
      updateSavedLanesState(response)
    } else {
      showErrorToast()
    }
  }, [
    createSavedLaneFromSearch,
    filters,
    motorCarrierId,
    showErrorToast,
    showSuccessToast,
    updateSavedLanesState,
  ])

  const deleteLane = useCallback(async () => {
    if (!savedLaneId) {
      return
    }

    const response = await deleteSavedLane(savedLaneId)
    if (response) {
      showSuccessToast()
      removeSavedLaneFromState(savedLaneId)
    } else {
      showErrorToast()
    }
  }, [savedLaneId, deleteSavedLane, showSuccessToast, removeSavedLaneFromState, showErrorToast])

  const switchChangeHandler = useCallback(() => {
    if (isLaneSaved) {
      deleteLane()
    } else {
      addLane()
    }
  }, [addLane, deleteLane, isLaneSaved])

  if (hasMultipleCarriers) {
    return null
  }

  return (
    <div className={classes.container}>
      <Typography variant='body2' component='div' className={classes.text}>
        Save this lane
      </Typography>
      <Tooltip
        position='right'
        trigger={
          <div className={classes.icon}>
            <InfoIcon color='coolGray5' />
          </div>
        }>
        Save your favorite lanes to get emails when loads matching your search criteria become
        available to book
      </Tooltip>
      <Switch
        dataTest='saved-lanes-switch'
        checked={isLaneSaved}
        disabled={loading}
        onChange={switchChangeHandler}
      />
    </div>
  )
})

export default SavedLaneSwitch
